// apps/web/src/features/russia-requests/components/editor/useRussiaPositionPhotos.ts
//
// Photo add/remove + upload-tracking sub-hook for the Russia SIMPLE position
// editor (#1545). Extracted from useRussiaDraftPositions.ts so the orchestrator
// stays under the file-size budget. Owns the in-flight upload ref outright:
// the orchestrator awaits a row's upload only through `awaitUpload`.

import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { REQUEST_ITEM_PHOTOS_MAX } from '@magimp/shared';
import { requestUploadUrl } from '../../../../api/requests/storage.js';
import {
  compressImage,
  imageSourceFormat,
  isPhotoCompressError,
  isUnsupportedSvgImage,
} from '../../../requests/draft-media/index.js';
import type { RussiaPositionRow } from './russiaPositions.model.js';

interface PhotosDeps {
  findRow: (localId: string) => RussiaPositionRow | undefined;
  mutateRow: (localId: string, fn: (row: RussiaPositionRow) => RussiaPositionRow) => void;
  schedulePatch: (localId: string) => void;
}

export interface RussiaPositionPhotosApi {
  addPhoto: (localId: string, file: File) => Promise<{ objectKey: string } | null>;
  removePhoto: (localId: string, key: string) => void;
  /** Resolves when a row's in-flight upload settles, or undefined if none. */
  awaitUpload: (localId: string) => Promise<void> | undefined;
  /** Awaits a snapshot of every currently in-flight upload across all rows. */
  awaitAllUploads: () => Promise<void>;
  /** Every object key whose PUT has settled this session, append-only and
   *   surviving photo/row removal — the discard-cleanup batch (#1561). */
  collectUploadedKeys: () => string[];
}

export function useRussiaPositionPhotos(deps: PhotosDeps): RussiaPositionPhotosApi {
  const { findRow, mutateRow, schedulePatch } = deps;
  const { t } = useTranslation('requests');

  // Per-row in-flight photo upload (localId → resolves when the upload
  //   settles). An explicit Save awaits this so the new photo key is part of
  //   the final write instead of being lost to a navigation race
  //   (#1545 round-6 COD-REV-002).
  const uploadInFlightRef = useRef<Map<string, Promise<void>>>(new Map());

  // Append-only accumulator of every object key whose storage PUT settled this
  //   session. Mirrors China's `trackUnsavedPhoto` "pin before write" pattern
  //   (#1561): the key is added the instant the object exists in storage, so a
  //   confirmed discard can GC it even if the cap re-check drops it from the
  //   row, the photo/row is later removed, or the component unmounts first.
  const uploadedKeysRef = useRef<Set<string>>(new Set());

  const addPhoto = useCallback(
    async (localId: string, file: File): Promise<{ objectKey: string } | null> => {
      const row = findRow(localId);
      if (!row || row.uploading || row.photos.length >= REQUEST_ITEM_PHOTOS_MAX) return null;
      if (isUnsupportedSvgImage(file)) {
        mutateRow(localId, (r) => ({
          ...r,
          uploadError: t('draft.photoClipboard.unsupportedType'),
        }));
        return null;
      }
      mutateRow(localId, (r) => ({ ...r, uploading: true, uploadError: null }));
      const work = (async (): Promise<{ objectKey: string } | null> => {
        try {
          const blob = await compressImage(file);
          const presigned = await requestUploadUrl({
            contentType: 'image/jpeg',
            contentLength: blob.size,
            scope: 'request-item-photo',
          });
          const putRes = await fetch(presigned.uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'content-type': 'image/jpeg' },
          });
          if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
          // Pin the key as uploaded BEFORE the cap re-check / mutateRow: the
          //   object now exists in storage, so a confirmed discard must clean
          //   it up even if it never lands on a row (#1561, China
          //   trackUnsavedPhoto "pin before write").
          uploadedKeysRef.current.add(presigned.objectKey);
          // Re-check the cap against the live row: a parallel save could
          //   have appended while this upload was in flight.
          const live = findRow(localId);
          if (!live || live.photos.length >= REQUEST_ITEM_PHOTOS_MAX) {
            mutateRow(localId, (r) => ({ ...r, uploading: false }));
            return null;
          }
          mutateRow(localId, (r) => ({
            ...r,
            uploading: false,
            photos: [...r.photos, presigned.objectKey],
          }));
          schedulePatch(localId);
          return { objectKey: presigned.objectKey };
        } catch (err) {
          let uploadError = t('draft.photoUploadFailed');
          if (isPhotoCompressError(err) && err.code === 'decode-failed') {
            uploadError = t('draft.photoDecodeFailed', { format: imageSourceFormat(file) });
          } else if (isPhotoCompressError(err) && err.code === 'encode-failed') {
            uploadError = t('draft.photoEncodeFailed');
          }
          mutateRow(localId, (r) => ({
            ...r,
            uploading: false,
            uploadError,
          }));
          return null;
        }
      })();
      // Register the in-flight upload so flushPending can await it. `work`
      //   never rejects (it catches internally), so this always settles.
      const tracked = work.then(() => undefined);
      uploadInFlightRef.current.set(localId, tracked);
      void tracked.finally(() => {
        if (uploadInFlightRef.current.get(localId) === tracked) {
          uploadInFlightRef.current.delete(localId);
        }
      });
      return work;
    },
    [findRow, mutateRow, schedulePatch, t],
  );

  const removePhoto = useCallback(
    (localId: string, key: string) => {
      mutateRow(localId, (r) => ({ ...r, photos: r.photos.filter((k) => k !== key) }));
      schedulePatch(localId);
    },
    [mutateRow, schedulePatch],
  );

  const awaitUpload = useCallback(
    (localId: string): Promise<void> | undefined => uploadInFlightRef.current.get(localId),
    [],
  );

  const awaitAllUploads = useCallback(async (): Promise<void> => {
    // Snapshot the values into an array first: each tracked promise's
    //   self-deleting `.finally` mutates the live map, so iterating it
    //   directly would skip entries that settle mid-await.
    await Promise.all([...uploadInFlightRef.current.values()]);
  }, []);

  const collectUploadedKeys = useCallback((): string[] => [...uploadedKeysRef.current], []);

  return { addPhoto, removePhoto, awaitUpload, awaitAllUploads, collectUploadedKeys };
}
